import React from 'react';
import { Field, useFormikContext } from 'formik';
import { FormGroup, TextboxField, SelectDropdownField, Text } from '@tradingblock/components';
import _ from 'lodash';
import styles from './SecurityQuestionRow.module.css';

interface SecurityQuestionRowProps {
  index: number;
  questionOptions: any;
  questionFieldId: string;
  answerFieldId: string;
  questionLabel: React.ReactNode;
  disabled?: boolean;
  menuPlacement?: 'auto' | 'top' | 'bottom';
  isSearchable?: boolean;
  openMenuOnFocus?: boolean;
}

const SecurityQuestionRow: React.FC<SecurityQuestionRowProps> = ({
  index,
  questionOptions,
  questionFieldId,
  answerFieldId,
  questionLabel,
  disabled,
  menuPlacement = 'auto',
  isSearchable = false,
  openMenuOnFocus = false,
}) => {
  const { errors, touched, submitCount } = useFormikContext();

  // Get error values for both fields
  const questionErrorValue = _.get(errors, questionFieldId) as string | undefined;
  const answerErrorValue = _.get(errors, answerFieldId) as string | undefined;

  // Determine if we should show errors (show when field is touched or after submit)
  const questionTouched = _.get(touched, questionFieldId);
  const answerTouched = _.get(touched, answerFieldId);

  const shouldShowQuestionError = questionErrorValue && (questionTouched || submitCount > 0);
  const shouldShowAnswerError = answerErrorValue && (answerTouched || submitCount > 0);

  // Get the error to display (prefer question error, then answer error)
  const displayError = shouldShowQuestionError
    ? questionErrorValue
    : shouldShowAnswerError
    ? answerErrorValue
    : undefined;
  const displayErrorFieldId = shouldShowQuestionError ? questionFieldId : answerFieldId;

  return (
    <>
      <div className={styles.row}>
        <div className={styles.colQuestion}>
          <FormGroup className={styles.formGroupFullHeight}>
            <div className={styles.fieldContainer}>
              <Field
                component={SelectDropdownField}
                id={questionFieldId}
                label={questionLabel}
                options={questionOptions}
                isSearchable={isSearchable}
                menuPlacement={menuPlacement}
                openMenuOnFocus={openMenuOnFocus}
                classNamePrefix="sq"
                disabled={disabled}
                errorAfterSubmit={false}
              />
            </div>
          </FormGroup>
        </div>

        <div className={styles.colAnswer}>
          <FormGroup className={styles.formGroupFullHeight}>
            <div className={styles.fieldContainer}>
              <Field
                component={TextboxField}
                id={answerFieldId}
                defaultLabelId="securityAnswer"
                disabled={disabled}
                errorAfterSubmit={false}
              />
            </div>
          </FormGroup>
        </div>
      </div>
      {displayError && (
        <div className={styles.rowError}>
          <span className="error">
            <Text id={displayErrorFieldId} textKey={`${displayError}`} type={'error'} />
          </span>
        </div>
      )}
    </>
  );
};

export default SecurityQuestionRow;
